import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetPromoCodeQuery, useGetPromoCodeStatsQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import { useLanguage } from '../../../context/LanguageContext';

const formatDate = (d: string | null | undefined) => {
  if (!d) return '-';
  const dt = new Date(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${dt.getDate().toString().padStart(2,'0')} ${months[dt.getMonth()]}, ${dt.getFullYear()}`;
};

const formatDateTime = (d: string | null | undefined) => {
  if (!d) return '-';
  const dt = new Date(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${dt.getDate().toString().padStart(2,'0')} ${months[dt.getMonth()]}, ${dt.getFullYear()} ${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`;
};

const AdminPromoCodeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [page, setPage] = useState(1);

  const { data: promoData, isLoading: promoLoading } = useGetPromoCodeQuery(id!);
  const { data: statsData, isLoading: statsLoading } = useGetPromoCodeStatsQuery(id!);

  if (promoLoading || statsLoading) return <PageLoader />;

  const promo = promoData?.data;
  const stats = statsData?.data;

  if (!promo) {
    return (
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="alert alert-danger m-5">{t('Promo code not found.')}</div>
      </div>
    );
  }

  const usages: any[] = stats?.usages?.data || [];
  const meta = stats?.usages || { current_page: 1, last_page: 1, total: 0 };

  const totalRedemptions  = stats?.total_redemptions  ?? 0;
  const uniqueUsers       = stats?.unique_users        ?? 0;
  const totalDiscount     = stats?.total_discount      ?? 0;
  const redemptionRate    = stats?.redemption_rate     ?? '0%';
  const avgDiscount       = stats?.avg_discount        ?? 0;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Promo Code Details')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted">{t('Home')}</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">
                  <button type="button" className="btn btn-link p-0 text-muted text-hover-primary" onClick={() => navigate('/admin/promo-codes')}>
                    {t('Promo Codes')}
                  </button>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Details')}</li>
              </ul>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-light-primary" onClick={() => navigate(`/admin/promo-codes/${id}/edit`)}>
                <i className="fas fa-pencil me-2"></i>{t('Edit')}
              </button>
              <button className="btn btn-sm btn-light" onClick={() => navigate('/admin/promo-codes')}>
                <i className={`fas ${lang === 'ar' ? 'fa-arrow-right' : 'fa-arrow-left'} me-2`}></i>{t('Back')}
              </button>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">

            {/* Stats Cards */}
            <div className="row g-5 mb-6">
              {[
                { label: t('Total Redemptions'), value: totalRedemptions, icon: 'fas fa-ticket-alt', color: 'primary' },
                { label: t('Unique Customers'), value: uniqueUsers,       icon: 'fas fa-users',      color: 'success' },
                { label: t('Total Discount Given'), value: `$${Number(totalDiscount).toFixed(2)}`, icon: 'fas fa-tags', color: 'warning' },
                { label: t('Avg. Discount / Use'),  value: `$${Number(avgDiscount).toFixed(2)}`,  icon: 'fas fa-percentage', color: 'info' },
                { label: t('Redemption Rate'),      value: redemptionRate, icon: 'fas fa-chart-line', color: 'danger' },
              ].map((s) => (
                <div key={s.label} className="col-6 col-md-4 col-xl">
                  <div className="card h-100">
                    <div className="card-body d-flex align-items-center gap-4 py-5">
                      <div className={`symbol symbol-50px symbol-light-${s.color} flex-shrink-0`}>
                        <span className={`symbol-label bg-light-${s.color}`}>
                          <i className={`${s.icon} fs-3 text-${s.color}`}></i>
                        </span>
                      </div>
                      <div>
                        <div className="fw-bold fs-4 text-gray-800">{s.value}</div>
                        <div className="text-muted fs-7">{s.label}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-5">
              {/* Promo Info */}
              <div className="col-xl-4">
                <div className="card mb-5">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title fw-bold fs-4">{t('Promo Details')}</h3>
                  </div>
                  <div className="card-body pt-0">
                    {[
                      { label: t('Title'),         value: promo.title },
                      { label: t('Title (AR)'),     value: promo.title_ar || '-' },
                      { label: t('Code'),          value: <span className="badge badge-light-primary fs-6 fw-bold">{promo.code}</span> },
                      { label: t('Discount'),      value: promo.discount_type === 'percentage' ? `$${promo.discount_value}%` : `$${promo.discount_value} (${t('fixed')})` },
                      { label: t('Min Order'),     value: promo.min_amount ?? '-' },
                      { label: t('Max Discount'),  value: promo.max_amount  ?? '-' },
                      { label: t('Usage / Customer'), value: promo.usage_per_customer ?? t('Unlimited') },
                      { label: t('Applicable To'), value: <span className="badge badge-light-info text-capitalize">{promo.user_type}</span> },
                      {
                        label: t('Expiry'),
                        value: promo.expiry_type === 'date'
                          ? formatDate(promo.expiry_date)
                          : promo.expiry_type === 'count'
                          ? `${promo.expiry_count} ${t('total uses')}`
                          : '-',
                      },
                      {
                        label: t('Status'),
                        value: (
                          <span className={`badge ${promo.status === 'active' ? 'badge-light-success' : 'badge-light-danger'}`}>
                            {promo.status ? t(promo.status) : t('active')}
                          </span>
                        ),
                      },
                      { label: t('Created'), value: formatDate(promo.created_at) },
                    ].map(({ label, value }) => (
                      <div key={label} className="d-flex justify-content-between align-items-center py-3 border-bottom border-gray-200">
                        <span className="text-muted fw-semibold fs-7">{label}</span>
                        <span className="fw-bold fs-7 text-gray-800 text-end">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Specific users if any */}
                {promo.users?.length > 0 && (
                  <div className="card">
                    <div className="card-header border-0 pt-6">
                      <h3 className="card-title fw-bold fs-4">{t('Assigned Users')}</h3>
                    </div>
                    <div className="card-body pt-0 d-flex flex-column gap-3">
                      {promo.users.map((u: any) => (
                        <div key={u.id} className="d-flex align-items-center gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center fw-bold text-white fs-7 flex-shrink-0"
                            style={{ width: 36, height: 36, borderRadius: '50%', background: '#396A54' }}
                          >
                            {u.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold text-gray-800 fs-7">{u.name}</div>
                            <div className="text-muted fs-8">{u.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Redemption History */}
              <div className="col-xl-8">
                <div className="card">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title fw-bold fs-4">{t('Redemption History')}</h3>
                    <div className="card-toolbar">
                      <span className="badge badge-light-primary">{meta.total || 0} {t('total uses')}</span>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed fs-6 gy-4">
                        <thead>
                          <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                            <th>#</th>
                            <th>{t('Customer')}</th>
                            <th>{t('Order / Ref')}</th>
                            <th>{t('Discount')}</th>
                            <th>{t('Used At')}</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-600 fw-semibold">
                          {usages.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-10">{t('No redemptions yet')}</td>
                            </tr>
                          ) : (
                            usages.map((u: any, i: number) => (
                              <tr key={u.id ?? i}>
                                <td>{(meta.current_page - 1) * 15 + i + 1}</td>
                                <td>
                                  <div className="fw-bold text-gray-800">{u.user?.name || '-'}</div>
                                  <div className="text-muted fs-7">{u.user?.email || ''}</div>
                                </td>
                                <td className="text-muted">{u.reference_id || u.order_id || '-'}</td>
                                <td>
                                  <span className="badge badge-light-success">
                                   $ {u.discount_amount != null ? Number(u.discount_amount).toFixed(2) : '-'}
                                  </span>
                                </td>
                                <td className="text-muted">{formatDateTime(u.used_at || u.created_at)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {meta.last_page > 1 && (
                      <Pagination
                        currentPage={meta.current_page}
                        lastPage={meta.last_page}
                        total={meta.total || 0}
                        onPageChange={(p) => { setPage(p); window.scrollTo(0, 0); }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPromoCodeDetails;
